/**
 * Care Plans Tab Component for Client Detail Page
 * Displays POC forms and Quick Care Plans
 */
import { Button } from "@/components/ui/button";
import { 
  FilePlus, ClipboardList, Plus, Eye, Edit, 
  PenTool, Link, Send, Download 
} from "lucide-react";

export function CarePlansTab({
  carePlanPOCs,
  carePlans,
  onNewPOC,
  onEditPOC,
  onViewReviews,
  onAddReview,
  onSignPOC,
  onSendSignLink,
  onFaxPOC,
  onDownloadPdf,
  onNewQuickPlan
}) {
  return (
    <div data-testid="client-care-plans-tab">
      <div className="space-y-6">
        {/* Plan of Care - PCW/Caregiver Section */}
        <div className="form-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Plan of Care - PCW/Caregiver ({carePlanPOCs.length})</h3>
              <p className="text-sm text-slate-500">Comprehensive care plans with ADLs, schedules, and signatures</p>
            </div>
            <Button
              size="sm"
              onClick={onNewPOC}
              className="gap-2"
              data-testid="new-care-plan-poc-btn"
            >
              <FilePlus className="w-4 h-4" />
              New POC Form
            </Button>
          </div>
          
          {carePlanPOCs.length === 0 ? (
            <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
              <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No Plan of Care forms created</p>
              <p className="text-xs mt-1">Click &quot;New POC Form&quot; to create a comprehensive care plan</p>
            </div>
          ) : (
            <div className="space-y-4">
              {carePlanPOCs.map((poc) => (
                <div
                  key={poc.id}
                  className="p-4 border border-slate-200 rounded-lg"
                  data-testid={`poc-card-${poc.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-slate-800">
                        Plan of Care - {poc.client_name}
                      </h4>
                      <p className="text-sm text-slate-500">
                        SOC: {poc.soc_date || 'N/A'} | {poc.hours_per_week || 0} hrs/week
                      </p>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      poc.status === 'signed' 
                        ? 'bg-emerald-100 text-emerald-700'
                        : poc.status === 'pending_signatures'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {poc.status === 'signed' ? 'Fully Signed' : poc.status === 'pending_signatures' ? 'Pending Signatures' : 'Draft'}
                    </span>
                  </div>
                  
                  {/* Schedule Info */}
                  <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                    <div>
                      <p className="text-xs text-slate-500">Days</p>
                      <p className="text-slate-700">{poc.days_of_week?.join(', ') || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Time</p>
                      <p className="text-slate-700">{poc.time_of_visits || 'Not specified'}</p>
                    </div>
                  </div>
                  
                  {/* RN Reviews Section */}
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-slate-500">
                        RN Reviews ({poc.rn_reviews?.length || 0})
                      </p>
                      <div className="flex gap-2">
                        {poc.rn_reviews?.length > 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 text-xs px-2"
                            onClick={() => onViewReviews(poc)}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View All
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-xs px-2 gap-1"
                          onClick={() => onAddReview(poc.id)}
                          data-testid={`add-rn-review-${poc.id}`}
                        >
                          <Plus className="w-3 h-3" />
                          Add Review
                        </Button>
                      </div>
                    </div>
                    {poc.rn_reviews?.length > 0 && (
                      <div className="bg-slate-50 p-2 rounded text-xs">
                        <p className="text-slate-500">Latest: {poc.rn_reviews[poc.rn_reviews.length - 1]?.date}</p>
                        <p className="text-slate-700 mt-1 line-clamp-2">
                          {poc.rn_reviews[poc.rn_reviews.length - 1]?.notes}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Signature Status */}
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs font-medium text-slate-500 mb-2">Signatures</p>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-slate-400">RN</p>
                        {poc.rn_signature ? (
                          <p className="text-emerald-600 text-xs font-medium">
                            Signed: {poc.rn_signer_name}
                          </p>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-1 h-7 text-xs gap-1"
                            onClick={() => onSignPOC(poc.id, "rn")}
                          >
                            <PenTool className="w-3 h-3" />
                            Sign
                          </Button>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Client/Rep</p>
                        {poc.client_signature ? (
                          <p className="text-emerald-600 text-xs font-medium">
                            Signed: {poc.client_signer_name}
                          </p>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-1 h-7 text-xs gap-1"
                            onClick={() => onSignPOC(poc.id, "client")}
                          >
                            <PenTool className="w-3 h-3" />
                            Sign
                          </Button>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">PCW/Caregiver</p>
                        {poc.pcw_signature ? (
                          <p className="text-emerald-600 text-xs font-medium">
                            Signed: {poc.pcw_signer_name}
                          </p>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-1 h-7 text-xs gap-1"
                            onClick={() => onSignPOC(poc.id, "pcw")}
                          >
                            <PenTool className="w-3 h-3" />
                            Sign
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <p className="text-xs text-slate-400">
                      Created: {poc.created_at?.split('T')[0] || 'N/A'}
                    </p>
                    <div className="flex gap-2">
                      {!(poc.rn_signature && poc.client_signature && poc.pcw_signature) && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-xs"
                          onClick={() => onEditPOC(poc)}
                          data-testid={`edit-poc-${poc.id}`}
                        >
                          <Edit className="w-3 h-3" />
                          Edit
                        </Button>
                      )}
                      {!poc.rn_signature && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-xs"
                          onClick={() => onSendSignLink("care_plan_poc", poc.id)}
                          data-testid={`sign-link-poc-${poc.id}`}
                        >
                          <Link className="w-3 h-3" />
                          Send Sign Link
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1 text-xs"
                        onClick={() => onFaxPOC("care_plan_poc", poc.id)}
                        data-testid={`fax-poc-${poc.id}`}
                      >
                        <Send className="w-3 h-3" />
                        eFax
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1"
                        onClick={() => onDownloadPdf(poc.id, poc.client_name)}
                        data-testid={`download-poc-pdf-${poc.id}`}
                      >
                        <Download className="w-4 h-4" />
                        PDF
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Simple Care Plans Section */}
        <div className="form-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Quick Care Plans ({carePlans.length})</h3>
              <p className="text-sm text-slate-500">Simple goals and interventions tracking</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={onNewQuickPlan}
              className="gap-2"
              data-testid="add-care-plan-btn"
            >
              <Plus className="w-4 h-4" />
              Quick Plan
            </Button>
          </div>
          
          {carePlans.length === 0 ? (
            <div className="text-center py-6 text-slate-500 bg-slate-50 rounded-lg">
              <p className="text-sm">No quick care plans created yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {carePlans.map((plan) => (
                <div
                  key={plan.id}
                  className="p-3 border border-slate-200 rounded-lg"
                  data-testid={`care-plan-${plan.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-slate-800">{plan.title}</h4>
                      <p className="text-sm text-slate-600 mt-1">{plan.goals}</p>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      plan.status === 'active' 
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {plan.status || 'Active'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
